import type { JSX, ReactNode } from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import {
  Dialog as DialogRoot,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle
} from '../components/ui/dialog.tsx'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'
import { cn } from '../lib/cn.ts'

/**
 * How much horizontal room a dialog takes, named after `Panel`'s own `width`
 * so the two read the same way at a call site.
 *
 * `form` matches `Panel`'s own `form` measure — the two must agree, because
 * this cap wins over the content (see the component note) and a `form` Panel
 * inside a wider dialog would just leave the extra room empty. `wide` is for
 * a dialog whose content is a table with enough columns that the form measure
 * would force a horizontal scroll a reader has to discover.
 */
const WIDTHS = {
  form: 'sm:max-w-2xl',
  wide: 'sm:max-w-5xl'
} as const satisfies Record<string, string>

export interface DialogProps {
  'data-testid': string
  /**
   * The dialog's accessible name.
   *
   * Required, because Radix announces a dialog with no title as nothing at
   * all and a reader who has just been moved into it cannot tell where they
   * are. It is not drawn — see the component note — so pass the same words
   * the content's own heading uses.
   */
  title: string
  /** Whether the dialog is on screen. The caller owns this state. */
  open: boolean
  /**
   * Called when the dialog asks to close: its own control, Escape, or a click
   * on the overlay. Radix also calls it with `true`, so a caller can pass a
   * setter straight in.
   */
  onOpenChange: (open: boolean) => void
  /**
   * Whether Escape and a click on the overlay may close this dialog.
   *
   * Default true. Pass false while the content holds something a close would
   * destroy — the create form's once-only password is the case this exists
   * for: the endpoint never returns it again, so an accidental Escape loses
   * it for good. The close control stays live either way, because pressing it
   * is deliberate.
   */
  dismissible?: boolean
  /** Override focus return when the dialog closes. */
  onCloseAutoFocus?: (event: Event) => void
  /**
   * How much horizontal room the dialog takes; see `WIDTHS`.
   *
   * Defaults to `form`, matching the `Panel` a dialog's content almost always
   * is. Pass `wide` when that content is a table wide enough that `form`
   * would force a horizontal scroll — and set the inner `Panel`'s own `width`
   * to `wide` too, since this cap wins over the content either way (see the
   * component note) and a `form` Panel inside a `wide` dialog just leaves the
   * extra room empty.
   */
  width?: keyof typeof WIDTHS
  /** The dialog's content. A `Panel`, in this console. */
  children: ReactNode
}

/**
 * Dialog — a modal over the console.
 *
 * The dialog owns the structural frame and fill. Its Panel stays flush, and
 * its title supplies the accessible name without a second visible heading.
 * Override the primitive through classes because the shadcn CLI owns it.
 *
 * The width caps at `props.width` (`form` by default), and that cap WINS over
 * the content: a `width="wide"` Panel inside a `form` dialog is still held to
 * the form measure. So a dialog that holds a table needs its own cap raised
 * to `wide` as well — the close control is positioned against this element,
 * so the box cannot simply be let go.
 *
 * @param props - Dialog props.
 * @param props.title - The dialog's accessible name.
 * @param props.open - Whether the dialog is on screen.
 * @param props.onOpenChange - Called when the dialog asks to close.
 * @param props.onCloseAutoFocus - Overrides focus return when the dialog closes.
 * @param props.dismissible - Whether Escape and the overlay may close it.
 * @param props.width - How much horizontal room to take; see `WIDTHS`.
 * @param props.children - The dialog's content.
 * @returns The rendered dialog.
 */
export function Dialog({
  'data-testid': testId,
  title,
  open,
  onOpenChange,
  dismissible = true,
  onCloseAutoFocus,
  width = 'form',
  children
}: DialogProps): JSX.Element {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {/*
        DialogContent paints its overlay with no class of our own, and that
        overlay is raw black. The portal is composed here so the scrim can
        take the ink token. The content classes are the ones DialogContent
        kept after the frame override.
      */}
      <DialogPortal>
        <DialogOverlay
          data-testid={`${testId}-overlay`}
          className="bg-ink-deep/70"
        />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          data-testid={testId}
          onCloseAutoFocus={onCloseAutoFocus}
          // Both routes to an ACCIDENTAL close, and both are refused together:
          // a caller that has something to protect has nothing to gain from
          // stopping one and not the other.
          onEscapeKeyDown={event => {
            if (!dismissible) {
              event.preventDefault()
            }
          }}
          onPointerDownOutside={event => {
            if (!dismissible) {
              event.preventDefault()
            }
          }}
          onInteractOutside={event => {
            if (!dismissible) {
              event.preventDefault()
            }
          }}
          className={cn(
            'fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'max-h-dvh overflow-y-auto rounded-none border-(length:--rule-width) border-rule bg-background p-6 shadow-none focus-visible:ring-0 motion-reduce:animate-none',
            WIDTHS[width]
          )}
        >
          <DialogTitle data-testid={`${testId}-title`} className="sr-only">
            {title}
          </DialogTitle>
          {children}
          {/*
          LAST in the DOM, though it is drawn at the top right. Radix moves
          focus to the first focusable descendant, and this must not be it:
          when the content holds something a close would destroy, the opening
          stop and the first Tab should reach the content's own controls —
          "Copy password" — not the one control that throws it away.
        */}
          <DialogClose asChild>
            <Button
              data-testid={`${testId}-close`}
              variant="quiet"
              size="icon-sm"
              className="absolute top-4 right-4"
            >
              <Icon name="x" />
              <span className="sr-only">{'Close'}</span>
            </Button>
          </DialogClose>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogRoot>
  )
}
